import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { PRODUCT_NAME } from '@web/core/app-info';
import { listenForStaleChunks } from '@web/core/app-version/stale-chunk';
import { mockedApiModules } from '@web/core/dev/api-mocks';
import { handleDemoExpired } from '@web/core/auth/demo';
import { handlePasswordChangeRequired } from '@web/core/auth/password-change';
import { isAuthPath } from '@web/core/auth/safe-next-path';
import { clearSessionState } from '@web/core/auth/session-cleanup';
import {
  setDemoExpiredHandler,
  setPasswordChangeRequiredHandler,
  setSessionExpiredHandler,
} from '@web/core/http/api';
import { createQueryClient } from '@web/core/http/query-client';
import { i18next, initI18n } from '@web/core/i18n/i18n';
import { AppToaster } from '@web/core/layouts/app-toaster';
import { PermissionsProvider } from '@web/core/permissions/permissions';
import { AppThemeProvider } from '@web/core/theme/theme';
import { routeTree } from './routeTree.gen';
import './styles.css';

// A tab from before a deploy reloads into the new version instead of breaking a screen.
listenForStaleChunks();

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // The Query cache already handles revalidation.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * The session is over (a refresh was refused): forget everything cached and go to the
 * login, coming back here afterwards. On the sign-in screens themselves, stay.
 */
setSessionExpiredHandler(() => {
  queryClient.clear();
  clearSessionState();
  const { pathname, href } = router.state.location;
  if (isAuthPath(pathname)) return;
  void router.navigate({ to: '/login', search: { next: href }, replace: true });
});

/** The API refused a call until the temporary password is changed: go change it. */
setPasswordChangeRequiredHandler(() => {
  handlePasswordChangeRequired(queryClient, router.state.location, (to) => {
    void router.navigate({ ...to, replace: true });
  });
});

/** The API refused a call because the demo period ended: the shell shows the notice. */
setDemoExpiredHandler(() => handleDemoExpired(queryClient));

/** Outside the app shell nobody holds a permission; `_app` provides the session's. */
const NO_PERMISSIONS: readonly string[] = [];

/**
 * Boot order: the mocked API modules (development only, `VITE_API_MOCKS`), then the
 * dictionary, then the tree — rendering without the dictionary would flash raw keys
 * (`shell.signOut`) before the text arrives, and a first request before the mocks start
 * would reach the real API.
 */
async function bootstrap(): Promise<void> {
  if (import.meta.env.DEV) {
    const modules = mockedApiModules();
    if (modules.length > 0) {
      const { startApiMocks } = await import('./mocks/browser');
      await startApiMocks(modules);
    }
  }
  await initI18n();
  document.title = PRODUCT_NAME;

  const root = document.querySelector('#root');
  if (!root) throw new Error('Missing #root element');

  createRoot(root).render(
    <StrictMode>
      <AppThemeProvider>
        <MotionConfig reducedMotion="user">
          <I18nextProvider i18n={i18next}>
            <QueryClientProvider client={queryClient}>
              <AppToaster />
              <PermissionsProvider permissions={NO_PERMISSIONS}>
                <RouterProvider router={router} />
              </PermissionsProvider>
            </QueryClientProvider>
          </I18nextProvider>
        </MotionConfig>
      </AppThemeProvider>
    </StrictMode>,
  );
}

void bootstrap();
